import React, { useState, useEffect } from "react";
import axios from "axios";
import { Globe, Eye, DownloadSimple, Rocket, Trash } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebsiteGallery() {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/websites`);
        setWebsites(res.data.websites || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleView = (id) => {
    window.open(`${API}/websites/${id}/html`, "_blank");
  };

  const handleDownload = async (website) => {
    try {
      const res = await axios.get(`${API}/websites/${website.id}`);
      const html = res.data.html_content || "";
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${website.business_name.replace(/\s+/g, "_").toLowerCase()}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="website-gallery-page" className="space-y-6">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tighter"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Website Gallery
        </h1>
        <p className="text-sm text-[#52525B] mt-1">
          All generated websites. Preview, download, or launch.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="spinner" />
        </div>
      ) : websites.length === 0 ? (
        <div className="bg-white border border-border p-12 text-center">
          <Globe weight="thin" size={48} className="mx-auto mb-3 text-[#A1A1AA]" />
          <p className="text-sm text-[#52525B]">No websites generated yet</p>
          <button
            onClick={() => navigate("/generator")}
            className="mt-3 text-xs font-semibold text-[#002FA7] hover:underline"
          >
            Generate your first website
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {websites.map((site, i) => (
            <div
              key={site.id}
              data-testid={`website-card-${i}`}
              className="bg-white border border-border hover:border-[#002FA7] transition-colors group animate-fade-in-up"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {/* Preview thumbnail */}
              <div className="h-40 bg-[#F5F5F7] border-b border-border overflow-hidden relative">
                {site.html_content ? (
                  <iframe
                    srcDoc={site.html_content}
                    className="w-[200%] h-[200%] border-none pointer-events-none"
                    style={{ transform: "scale(0.5)", transformOrigin: "top left" }}
                    title={site.business_name}
                    sandbox=""
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#A1A1AA]">
                    <Globe weight="thin" size={32} />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span
                    className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                      site.status === "ready"
                        ? "bg-[#00C853] text-white"
                        : site.status === "launched"
                        ? "bg-[#002FA7] text-white"
                        : "bg-[#E5E5E5] text-[#52525B]"
                    }`}
                  >
                    {site.status}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h4 className="font-bold text-sm truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  {site.business_name}
                </h4>
                <p className="text-xs text-[#52525B] mt-0.5 truncate">
                  {site.industry} &middot; {site.keywords}
                </p>
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {new Date(site.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* Actions */}
              <div className="flex border-t border-border">
                <button
                  data-testid={`view-site-${i}`}
                  onClick={() => handleView(site.id)}
                  className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold hover:bg-[#F5F5F7] transition-colors border-r border-border"
                >
                  <Eye weight="bold" size={13} />
                  View
                </button>
                <button
                  data-testid={`download-site-${i}`}
                  onClick={() => handleDownload(site)}
                  className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold hover:bg-[#F5F5F7] transition-colors border-r border-border"
                >
                  <DownloadSimple weight="bold" size={13} />
                  Download
                </button>
                <button
                  data-testid={`launch-site-${i}`}
                  onClick={() => navigate(`/launch?websiteId=${site.id}`)}
                  className="flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-semibold text-[#002FA7] hover:bg-[#EEF2FF] transition-colors"
                >
                  <Rocket weight="bold" size={13} />
                  Launch
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
